import React, { useEffect, useRef } from 'react';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import {
	useAnalyticsEvents as useAnalyticsEventsDI,
	fireUIAnalytics as fireUIAnalyticsDI,
} from '@atlassian/jira-product-analytics-bridge';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { useSpaStateTransition as useSpaStateTransitionDI } from '@atlassian/jira-spa-state-controller/src/components/transition-state/index.tsx';
import {
	useResource as useResourceDI,
	useRouter as useRouterDI,
	Redirect as RedirectDI,
} from '@atlassian/react-resource-router';
import type { RedirectProps } from './types.tsx';
/**
 * Manages the redirection process to the appropriate project type page within a web application.
 * This function dynamically constructs the URL for redirection based on the project's characteristics and
 * the current context, such as type and key. It also handles the analytics for the redirection event,
 * ensuring meaningful interaction data is captured efficiently.
 */
export const RedirectToProjectType = ({
	Redirect = RedirectDI,
	fireUIAnalytics = fireUIAnalyticsDI,
	page,
	// eslint-disable-next-line react-hooks/react-compiler
	useAnalyticsEvents = useAnalyticsEventsDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useResource = useResourceDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useRouter = useRouterDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useSpaStateTransition = useSpaStateTransitionDI,
}: RedirectProps): React.JSX.Element | null => {
	// eslint-disable-next-line react-hooks/react-compiler
	const [{ match, query, route }] = useRouter();
	// eslint-disable-next-line react-hooks/react-compiler
	const { data } = useResource(projectContextResource);
	// eslint-disable-next-line react-hooks/react-compiler
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// eslint-disable-next-line react-hooks/react-compiler
	const [{ isInitialRender: isInitial }] = useSpaStateTransition();
	const eventRef = useRef(createAnalyticsEvent({}));
	useEffect(() => {
		if (!data || !route || !eventRef.current) return;
		fireUIAnalytics(eventRef.current, 'route redirected', {
			isInitial,
			routeName: route.name,
		});
		// @ts-expect-error - Type 'null' is not assignable to type 'UIAnalyticsEvent'.
		eventRef.current = null;
	}, [data, fireUIAnalytics, isInitial, route]);
	if (!data || !route) return null;
	const { projectKey, projectType, simplified } = data;
	let type;
	switch (projectType) {
		case SERVICE_DESK_PROJECT:
			type = 'servicedesk';
			break;
		case CORE_PROJECT:
			type = 'core';
			break;
		case SOFTWARE_PROJECT:
		default:
			type = `software${simplified ? '' : '/c'}`;
	}
	const returnPage = typeof page === 'string' ? page : page(match, query);
	return <Redirect to={`/jira/${type}/projects/${projectKey}${returnPage}`} />;
};
